<template>
  <div class="location-search-outer">
    <div class="selectedLocation" v-if="hasLocation">
      <div class="content-item">
        <flex-row gap center>
          <flex-cell shrink>
            <div class="image-wrapper">
              <ux-icon icon="fa-map-marker" />
            </div>
          </flex-cell>

          <flex-cell>
            <div class="content-item-title">
              {{ model.title || model.addressLine1 }}
            </div>
            <div class="content-item-subtitle" v-if="summary">
              {{ summary(model) }}
            </div>
            <!-- <div class="content-item-coordinates"><ux-icon icon="fa-map-marker" left/> {{model.latitude}}, {{model.longitude}}</div> -->
          </flex-cell>
          <flex-cell shrink v-if="showAdvancedToggle">
            <ux-button size="sm" @click="toggleAdvanced">
              {{ advanced ? "Less detail" : "More detail" }}
              <ux-icon
                right
                :icon="advanced ? 'fa-angle-up' : 'fa-angle-down'"
              />
            </ux-button>
          </flex-cell>
          <flex-cell shrink v-if="deselectEnabled">
            <ux-button icon @click="deselect">
              <ux-icon icon="fa-times" />
            </ux-button>
          </flex-cell>
        </flex-row>
      </div>
    </div>
    <template v-else>
      <search
        @touched="touch"
        v-model="keywords"
        @keydown="handleKeyNavigation"
        placeholder="Search for a location"
        :loading="processing"
      />

      <teleport :disabled="!teleportTarget" :to="teleportTarget">
        <div v-if="results.length" class="results">
          <div
            class="result-item"
            v-for="(item, index) in results"
            :key="item.title"
            @click="select(item)"
            :class="{ selected: index === selectedIndex }"
          >
            <div class="result-item-title">{{ item.title }}</div>
            <div class="result-item-subtitle">{{ item.summary }}</div>
          </div>
        </div>
      </teleport>
    </template>
  </div>
</template>
<script>
import Item from "../../content/item.vue";
import Spinner from "../../ui/spinner.vue";
import Search from "./search.vue";

export default {
  emits: ["deselect"],
  components: {
    Search,
    Spinner,
  },
  props: {
    deselectEnabled: {
      type: Boolean,
      default: true,
    },
    showAdvancedToggle: {
      type: Boolean,
    },
    advanced: {
      type: Boolean,
    },
    teleportID: {
      type: [String, Number],
    },
    modelValue: {
      type: Object,
      default() {
        return {
          search: undefined,
          latitude: undefined,
          longitude: undefined,
          threshold: undefined,
          title: undefined,
          addressLine1: undefined,
          addressLine2: undefined,
          suburb: undefined,
          state: undefined,
          country: undefined,
          postalCode: undefined,
        };
      },
    },
  },
  computed: {
    hasLocation() {
      return (
        this.model.addressLine1 ||
        (this.model?.longitude && this.model?.latitude)
      );
    },
    teleportTarget() {
      if (this.teleportID) {
        return `#teleport-${this.teleportID}`;
      }
    },
    keywords: {
      get() {
        return this.model.search || "";
      },
      set(k) {
        this.model.search = k || "";
      },
    },
  },
  data() {
    return {
      selectedIndex: -1,
      results: [],
      processing: false,
      model: this.modelValue,
      showAdvanced: this.advanced,
    };
  },
  watch: {
    keywords: "findResults",
    modelValue(m) {
      this.model = m;
    },
    model(m) {
      console.log("emit up!", m);
      this.$emit("update:modelValue", m);
    },
  },
  methods: {
    summary(input) {
      if (!input) {
        return "";
      }

      const addressPieces = [
        input.addressLine1,
        input.suburb,
        input.state,
        input.postalCode,
        input.country,
      ].filter(Boolean);
      if (addressPieces) {
        return addressPieces.join(", ");
      }

      return [input.latitude, input.longitude].filter(Boolean).join(", ");
    },
    toggleAdvanced() {
      this.showAdvanced = !this.showAdvanced;
      this.$emit("update:advanced", this.showAdvanced);
    },
    deselect() {
      this.model = {
        title: undefined,
        longitude: undefined,
        latitude: undefined,
        search: undefined,
        threshold: 0,
        addressLine1: undefined,
        addressLine2: undefined,
        suburb: undefined,
        state: undefined,
        country: undefined,
        postalCode: undefined,
      };

      this.$emit("deselect");
    },
    handleKeyNavigation(event) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        this.selectedIndex = (this.selectedIndex + 1) % this.results.length;
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        this.selectedIndex =
          (this.selectedIndex - 1 + this.results.length) % this.results.length;
      } else if (event.key === "Enter") {
        if (this.selectedIndex !== -1) {
          this.select(this.results[this.selectedIndex]);
        }
      }
    },
    async select(item) {
      if (!item) {
        return;
      }

      this.model = Object.assign(
        {
          threshold: 0,
        },
        this.model,
        {
          longitude: item.longitude,
          latitude: item.latitude,
          title: item.title || "",
          search: "",
          addressLine1: item.addressLine1 || item.address,
          addressLine2: item.addressLine2 || "",
          suburb: item.suburb || "",
          state: item.state || "",
          country: item.country || "",
          postalCode: item.postalCode || "",
        },
      );

      this.keywords = "";
    },
    async findResults() {
      const self = this;
      self.selectedIndex = -1;

      const keywords = self.keywords;
      if (!keywords?.length) {
        self.results = [];
        self.processing = false;
        return;
      }

      self.processing = true;

      const { data } = await self.$sdk.api.get(
        `/geo/places/search/${keywords}`,
      );
      self.results = (data.locations || []).map(function ({ formatted }) {
        return {
          ...formatted,
          summary: self.summary(formatted),
        };
      });
      self.processing = false;
    },
  },
};
</script>
<style lang="scss" scoped>
$bcolor: color-mix(in lab, currentColor 15%, transparent);
.results {
  width: 100%;
  background: var(--bg1, #fff);
  border: 1px solid $bcolor;
  border-radius: 0.5em;
  overflow: hidden;
  box-shadow: 0 2px 5px rgb(0 0 0 / 15%);
  position: absolute;
  z-index: 2;
}

.location-search-outer {
  position: relative;
}
.result-item {
  cursor: pointer;
  padding: 0.5em;
  border-bottom: 1px solid $bcolor;

  &:last-child {
    border-bottom: none;
  }

  &.selected {
    background: var(--primary);
    color: #fff;

    .result-item-title {
      font-weight: 500;
    }
  }
}

.content-item-title {
  font-weight: 500;
}

.content-item-coordinates,
.content-item-subtitle,
.result-item-subtitle {
  font-size: 0.9em;
  opacity: 0.6;
}

.content-item-coordinates {
  font-size: 0.8em;
}

.content-item {
  padding: 0.5em 0.8em;
  border-radius: 0.3em;
  cursor: pointer;
  margin-bottom: 0.5em;

  .image-wrapper {
    width: 2em;
    height: 2em;
    line-height: 2em;
    margin-right: 0.5em;
    text-align: center;
    border-radius: 100%;
    background: rgba(#000, 0.05);
  }

  @media (prefers-color-scheme: light) {
    background: var(--bg1, #fff);
    border: 1px solid color-mix(in lab, currentColor 15%, transparent);
  }

  @media (prefers-color-scheme: dark) {
    background: color-mix(in lab, #000 1%, transparent);
    border: 1px solid color-mix(in lab, currentColor 18%, transparent);
  }

  .text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: pre-line;
  }

  // &:hover {
  //     cursor: pointer;
  //     background: color-mix(in lab, currentColor 2%, transparent);
  // }
}
</style>
